import React from 'react';
import dayjs from 'dayjs';
import { Typography } from 'antd';
import {
  PlusOutlined,
  DownloadOutlined,
  BarChartOutlined,
  SettingOutlined,
  TeamOutlined,
  FileZipOutlined
} from '@ant-design/icons';

import { useBanners } from '../context/banners';
import QuickAccess from '../components/dashboard/quick-access';
import RecentBannersList from '../components/dashboard/recent-banners';
import { MenuSections } from '../types/menu-sections';
import { usePolling } from '../hooks/use-polling';
import { useResponsiveSizes } from '../hooks/use-responsive-sizes';
import { BannerExport } from '../components/library/banner-export';
import { useSettingsContext } from '../context/settings';

const { Title } = Typography;

interface DashboardPageProps {
  onNavigate: (section: MenuSections) => void;
}

export const DashboardPage: React.FC<DashboardPageProps> = ({ onNavigate }) => {
  const { settings } = useSettingsContext();
  const { fontSize, spacing, screens } = useResponsiveSizes();
  const { allBanners, isRefreshingAll, refreshAllBanners } = useBanners();

  const [isHTML5ModalOpen, setHTML5ModalOpen] = React.useState(false);

  const recentBanners = allBanners
    .sort((a, b) => dayjs(b.createdAt).valueOf() - dayjs(a.createdAt).valueOf())
    .slice(0, 5);

  const accesosItems = [
    {
      key: '1',
      title: 'Crear nuevo asset',
      description: 'Diseñar desde cero o usa un template',
      icon: <PlusOutlined style={{ fontSize: '18px', color: '#FF7A00' }} />,
      onClick: () => onNavigate('create-asset'),
      disabled: settings.disableBannerCreation
    },
    {
      key: '1-html5',
      title: 'Exportar Banners',
      description: 'Generar archivos HTML5',
      icon: <FileZipOutlined style={{ fontSize: '18px', color: '#FF7A00' }} />,
      onClick: () => setHTML5ModalOpen(true),
      disabled: settings.disableBannerCreation
    },
    {
      key: '2',
      title: 'Descargar reportes',
      description: 'Exportar estadísticas detalladas',
      icon: <DownloadOutlined style={{ fontSize: '18px', color: '#2F80ED' }} />,
      onClick: () => {},
      disabled: true
    },
    {
      key: '3',
      title: 'Ver analíticas detalladas',
      description: 'Métricas completas de rendimiento',
      icon: <BarChartOutlined style={{ fontSize: '18px', color: '#27AE60' }} />,
      onClick: () => {},
      disabled: true
    },
    {
      key: '4',
      title: 'Configuración de cuenta',
      description: 'Ajustar preferencias y configuración',
      icon: <SettingOutlined style={{ fontSize: '18px', color: '#9B51E0' }} />,
      onClick: () => {},
      disabled: true
    },
    {
      key: '5',
      title: 'Gestionar equipo',
      description: 'Invitar usuarios y administrar roles',
      icon: <TeamOutlined style={{ fontSize: '18px', color: '#E6776D' }} />,
      onClick: () => {},
      disabled: true
    }
  ];

  const hasIncompleteBanners = React.useMemo(() => {
    return recentBanners.some(banner => banner.status !== 'DONE');
  }, [recentBanners]);

  // Polling solo cuando hay banners incompletos
  usePolling({
    onPoll: refreshAllBanners,
    enabled: hasIncompleteBanners,
    interval: 10000
  });

  return (
    <div style={{ maxWidth: '100%', margin: `0 ${spacing.xxl}px` }}>
      <Title style={{ fontSize: fontSize.xxxl, marginBottom: spacing.xs, display: 'flex' }}>Dashboard</Title>
      <Typography.Text type='secondary' style={{ fontSize: fontSize.xl, display: 'flex', marginBottom: spacing.sm }}>
        Panel de control de tu marca
      </Typography.Text>

      <div style={{ display: 'flex', gap: spacing.md, flexDirection: screens.md ? 'row' : 'column' }}>
        <QuickAccess items={accesosItems} />
        <RecentBannersList
          items={recentBanners}
          onRefresh={refreshAllBanners}
          loading={isRefreshingAll}
          onNavigate={onNavigate}
        />
      </div>
      <BannerExport open={isHTML5ModalOpen} onClose={() => setHTML5ModalOpen(false)} />
    </div>
  );
};

export default DashboardPage;
